/** In-app chat / team messaging (v1 scraper-builder.js messagesView). v1's page
 * was a shell — its MSGS array was always empty. v2 makes it real by aggregating
 * the conversations that actually exist: every wholesale deal's team chat and
 * its logged calls/texts/emails, into one searchable feed with All / @Mentions
 * tabs. Pure — the page just renders + filters. */
import type { WholesaleDeal } from "../wholesale/api";

export type MessageKind = "chat" | "call" | "text" | "email";

export interface MessageEntry {
  key: string;
  natKey: string; // the wholesale deal, for navigation
  address: string;
  kind: MessageKind;
  sender: string; // chat author initial, or comm type label
  text: string;
  mentions: boolean; // contains an @mention
}

const commKind = (t: string): MessageKind =>
  /call/i.test(t) ? "call" : /text/i.test(t) ? "text" : /email/i.test(t) ? "email" : "chat";

/** Flatten every wholesale deal's chat + comms into one feed. Order follows the
 * input (wholesale is fetched updated-desc); within a deal, comms are already
 * newest-first (v1 unshift) and chat oldest-first (v1 push). */
export function aggregateMessages(wholesale: WholesaleDeal[]): MessageEntry[] {
  const out: MessageEntry[] = [];
  for (const w of wholesale) {
    w.comms.forEach((c, i) => {
      const text = c.m.trim();
      if (!text) return;
      out.push({ key: `${w.natKey}:comm:${i}`, natKey: w.natKey, address: w.address, kind: commKind(c.t), sender: c.t || "Note", text, mentions: text.includes("@") });
    });
    w.chat.forEach((c, i) => {
      const text = c.m.trim();
      if (!text) return;
      out.push({ key: `${w.natKey}:chat:${i}`, natKey: w.natKey, address: w.address, kind: "chat", sender: c.who || "?", text, mentions: text.includes("@") });
    });
  }
  return out;
}

export function filterMessages(entries: MessageEntry[], tab: "all" | "mentions", q: string): MessageEntry[] {
  const needle = q.trim().toLowerCase();
  return entries.filter((e) => {
    if (tab === "mentions" && !e.mentions) return false;
    if (needle && !(`${e.address} ${e.sender} ${e.text}`.toLowerCase().includes(needle))) return false;
    return true;
  });
}

export const messageEmoji = (kind: MessageKind): string =>
  kind === "call" ? "📞" : kind === "text" ? "💬" : kind === "email" ? "✉️" : "🗨️";
